import React, { useMemo } from "react"
import { Image, StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import moment from "moment"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import ObjectiveSubjectsBadges from "../../../common/components/objectives/ObjectiveSubjectsBadges"
import appPreviewImage from "@assets/offers/app-preview.png"

const formatTimeSlot = (course) => {
    const slots = course?.slots
    const duration = course?.live_session_duration
    if (!slots?.length || !duration) return null
    const timePart = slots[0].split(" ")[1]
    if (!timePart) return null
    const [hours, minutes] = timePart.split(":").map(Number)
    const formatHour = (h, m) =>
        m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`
    const startH = formatHour(hours, minutes)
    const endMinutes = hours * 60 + minutes + duration
    const endH = formatHour(Math.floor(endMinutes / 60), endMinutes % 60)
    return `${startH}-${endH}`
}

const InfoRow = ({ icon, children }) => (
    <View style={styles.infoRow}>
        <View style={styles.infoIcon}>
            <FontAwesome5
                name={icon}
                solid
                size={14}
                color={theme.colors.primary}
            />
        </View>
        <TextM fontSize={15} color={theme.colors.grey} wrap>
            {children}
        </TextM>
    </View>
)

export const StageRecapCard = ({
    schoolSubjects,
    holidayCourses,
    amountToPay,
    subscriptionMetadata,
}) => {
    const selectedHolidayCourses = useMemo(() => {
        if (!subscriptionMetadata?.holiday_courses) return null
        return subscriptionMetadata.holiday_courses.map((hc) => {
            const details = holidayCourses?.find((h) => h.id === hc.id)
            return {
                ...details,
                school_subject_ids: hc.school_subject_ids ?? [],
            }
        })
    }, [subscriptionMetadata?.holiday_courses, holidayCourses])

    return (
        <View style={[styles.card]}>
            <Image
                source={appPreviewImage}
                style={{ width: "100%", height: 250, borderRadius: 10 }}
                resizeMode="contain"
            />
            <TextM fontWeight="bold" fontSize={20} color={theme.colors.black}>
                Stage de révision
            </TextM>

            <View style={styles.infoStack}>
                <InfoRow icon="user-graduate">
                    Programme 100% personnalisé
                </InfoRow>
                <InfoRow icon="users">Groupes de 3 élèves max</InfoRow>
            </View>

            {selectedHolidayCourses?.map((course, index) => {
                const timeSlot = formatTimeSlot(course)
                return (
                    <View key={index} style={styles.stack}>
                        <InfoRow icon="calendar-alt">
                            Du{" "}
                            <TextM fontSize={15} fontWeight="Bold">
                                {moment(course?.start_date).format("DD/MM")}
                            </TextM>{" "}
                            au{" "}
                            <TextM fontSize={15} fontWeight="Bold">
                                {moment(course?.end_date).format("DD/MM/YYYY")}
                            </TextM>
                            {timeSlot && (
                                <>
                                    {"  ·  "}
                                    <TextM fontSize={15} fontWeight="Bold">
                                        {timeSlot}
                                    </TextM>
                                </>
                            )}
                        </InfoRow>
                        <View style={styles.row}>
                            <TextM fontSize={16} fontWeight="Medium">
                                Matières :
                            </TextM>
                            <ObjectiveSubjectsBadges
                                objective={{
                                    subject_ids: course?.school_subject_ids,
                                    type: "level_up",
                                }}
                                schoolSubjects={schoolSubjects}
                                tinyMode={false}
                                truncateNames={false}
                            />
                        </View>
                    </View>
                )
            })}

            {amountToPay ? (
                <>
                    <View style={styles.separator} />
                    <View style={styles.row}>
                        <TextM fontSize={18} fontWeight="Bold">
                            à régler maintenant
                        </TextM>
                        <TextM
                            fontSize={24}
                            color={theme.colors.primary}
                            fontWeight="Bold"
                        >
                            {amountToPay}€
                        </TextM>
                    </View>
                </>
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: theme.colors.white,
        padding: 20,
        borderRadius: 20,
        gap: 15,
        elevation: 4,
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
    },
    separator: {
        height: 1,
        backgroundColor: theme.colors.lightMore,
    },
    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
    },
    stack: {
        gap: 8,
    },
    infoStack: {
        gap: 10,
    },
    infoRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    infoIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor:
            theme.colors.primaryLighter ?? theme.colors.linkLighter,
        alignItems: "center",
        justifyContent: "center",
    },
})
